import React, { PropTypes, PureComponent } from 'react';
import SaveCancelModal from './SaveCancelModal';
import TextInput from '../inputs/TextInput';
import Toggle from 'react-toggle';
import { CourtSelect, FILTER_DATA_SHAPE, GameGroupSelect } from '../../GameDay/components/forms/AdminGamesFilter';
import TourneyTeamSelect from '../inputs/TourneyTeamSelect';
import GameSelect from '../inputs/GameSelect';
import FormControl from '../inputs/FormControl';
import cx from 'classnames';
import DateTimeInput from '../inputs/DateTimeInput';
import { extractZoneId } from '../../util/EventUtil';
import moment from 'moment-timezone';
import PageTabSections from '../layout/PageTabSections';
import RequiredIndicator from '../layout/RequiredIndicator';

const NumberInput = props => <FormControl><input type="number" {...props}/></FormControl>;
const RANK_STYLE = { width: 80, paddingLeft: 8, paddingRight: 8 };
const LOCAL_TIMEZONE = moment.tz.guess();

const WinLoseSelect = props => (
  <FormControl>
    <select {...props}>
      <option value="">N/A</option>
      <option value="1">Winner</option>
      <option value="2">Loser</option>
    </select>
  </FormControl>
);

const PoolRankSelect = props => (
  <FormControl>
    <select {...props}>
      <option value="">N/A</option>
      <option value="1">1st</option>
      <option value="2">2nd</option>
      <option value="3">3rd</option>
      <option value="4">4th</option>
      <option value="5">5th</option>
      <option value="6">6th</option>
      <option value="7">7th</option>
      <option value="8">8th</option>
      <option value="9">9th</option>
      <option value="10">10th</option>
    </select>
  </FormControl>
);

export const GroupSelectWithDivision = props => (
  <GameGroupSelect valueToString={({ type, name, division }) => `${type}: ${name} - ${division.name}`} {...props}/>
);

const RankSelect = ({ value, onChange }) => {
  const Component = value && value.sourceGame ? WinLoseSelect : PoolRankSelect;

  return (
    <Component
      placeholder="Rank" style={RANK_STYLE} value={value && value.rank ? value.rank : ''}
      onChange={({ target: { value: rank } }) => onChange(rank && rank.length ? { ...value, rank } : null)}/>
  );
};

const SeedForm = ({ value, onChange, className, filterData: { tourney, gameGroups }, ...rest }) => (
  <div className={cx('display-flex', className)} {...rest}>
    <div className="mg-right-sm">
      <label>Rank</label>
      <RankSelect value={value} onChange={onChange}/>
    </div>

    <div className="flex-grow-1 mg-right-sm">
      <label>Game</label>
      <GameSelect
        params={{ tourneyId: tourney.id }}
        onChange={sourceGame => onChange({
          ...value,
          sourceGame,
          sourcePool: null,
          rank: sourceGame === null ? value.rank : Math.min(2, value.rank)
        })}
        disabled={value === null}
        value={value ? value.sourceGame : null}/>
    </div>

    <div className="flex-grow-1">
      <label>Pool</label>
      <GroupSelectWithDivision
        disabled={value === null}
        options={_.filter(gameGroups, ({ type }) => type === 'Pool')}
        value={value ? value.sourcePool : null}
        onChange={sourcePool => onChange({ ...value, sourcePool, sourceGame: null })}/>
    </div>
  </div>
);

const ScoreForm = ({ value, onChange, className, disabled, ...rest }) => (
  <div className={cx('display-flex', className)} {...rest}>
    <div className="flex-shrink-0 mg-right-sm">
      <label>Score</label>
      <div>
        <NumberInput
          className="hide-number-input-arrows"
          disabled={disabled}
          placeholder="#"
          style={{ width: 60, paddingLeft: 8, paddingRight: 8 }} value={value && value.score ? value.score : ''}
          type="number" pattern="\d*" min={0} max={1000}
          onChange={({ target: { value: score } }) => onChange(score && score.length ? { ...value, score } : null)}/>
      </div>
    </div>
    <div className="flex-grow-1">
      <label>Notes</label>
      <div>
        <TextInput value={value && value.notes ? value.notes : ''} type="text" disabled={!value || disabled}
          placeholder="score notes..."
          onChange={({ target: { value: notes } }) => onChange({ ...value, notes })}/>
      </div>
    </div>
  </div>
);

const SMALL_HR = <hr className="mg-top-sm mg-bottom-sm"/>;

const SideForm = ({ label, value = {}, onChange, filterData, className }) => (
  <div className={cx('pd-sm', className)} style={{ border: '1px solid' }}>
    <div className="display-flex align-items-center">
      <div className="flex-grow-1 mg-right-sm">
        <label>{label} Team</label>
        <TourneyTeamSelect showNumGames={false} required={false} teams={filterData.teams} value={value.team}
          multi={false}
          onChange={team => onChange({ ...value, team, score: team === null ? null : value.score })}/>
      </div>
    </div>

    {SMALL_HR}

    <ScoreForm value={value.score} onChange={score => onChange({ ...value, score })} disabled={!value.team}/>

    {SMALL_HR}

    <SeedForm value={value.seed} onChange={seed => onChange({ ...value, seed })} filterData={filterData}/>
  </div>
);

class GameForm extends PureComponent {
  static propTypes = {
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    filterData: FILTER_DATA_SHAPE.isRequired
  };

  render() {
    const { value, onChange, filterData } = this.props;
    const changed = change => onChange({ ...value, ...change });

    const { courts, gameGroups } = filterData;
    const {
      name,
      externalId,
      cancelled,
      bracketLabel,
      ignoreStandings,
      gameGroup,
      court,
      sides = {},
      scheduled
    } = value;

    // just use a default if we can't tell what tourney the game is in
    const timezone = extractZoneId(value) || LOCAL_TIMEZONE;


    return (
      <PageTabSections defaultActive={1} options={[
        {
          key: 'details',
          label: 'DETAILS',
          section: (
            <div>
              <div className="display-flex align-items-flex-end flex-wrap-wrap">
                <div className="flex-grow-2 mg-right-sm mg-top-sm">

                  <label>Name <RequiredIndicator/></label>
                  <TextInput placeholder="Name" required value={name}
                    onChange={e => changed({ name: e.target.value })}/>
                </div>

                <div className="flex-grow-1 mg-right-sm mg-top-sm">
                  <label>Bracket Label</label>
                  <TextInput placeholder="Bracket Label" value={bracketLabel}
                    onChange={e => changed({ bracketLabel: e.target.value })}/>
                </div>

                <div className="flex-grow-1 mg-right-sm mg-top-sm">
                  <label>External ID</label>
                  <TextInput placeholder="External ID" value={externalId} disabled
                    onChange={e => changed({ externalId: e.target.value })}/>
                </div>

                <div className="mg-right-sm mg-top-sm">
                  <label>Cancelled</label>
                  <div>
                    <Toggle checked={Boolean(cancelled)} onChange={e => changed({ cancelled: e.target.checked })}/>
                  </div>
                </div>

                <div className="mg-top-sm">
                  <label>Standings</label>
                  <div>
                    <Toggle checked={!ignoreStandings} onChange={e => changed({ ignoreStandings: !e.target.checked })}/>
                  </div>
                </div>
              </div>

              <div className="display-flex flex-wrap-wrap mg-top-sm align-items-center">
                <div className="flex-shrink-0 mg-right-sm">
                  <label>Timezone</label>
                  <div>{timezone}</div>
                </div>
                <div className="flex-grow-1">
                  <DateTimeInput timezone={timezone} onChange={scheduled => changed({ scheduled })}
                    clearable={false} value={scheduled} required={true}/>
                </div>
              </div>

              <div className="display-flex mg-top-sm">
                <div className="mg-right-sm flex-grow-1">
                  <label>Pool/Bracket <RequiredIndicator/></label>
                  <GroupSelectWithDivision
                    required={true} value={gameGroup} options={gameGroups}
                    onChange={gameGroup => changed({ gameGroup })}/>
                </div>
              </div>

              <div className="display-flex mg-top-sm">
                <div className="mg-right-sm flex-grow-1">
                  <label>Court <RequiredIndicator/></label>
                  <CourtSelect
                    valueToString={({ name, venue }) => `${name} - ${venue.name}`}
                    required={true} clearable={false} value={court} options={courts}
                    onChange={court => changed({ court })}
                  />
                </div>
              </div>
            </div>
          )
        },
        {
          key: 'sides',
          label: 'SIDES',
          section: (
            <div className="display-flex flex-wrap-wrap">
              <SideForm label="Home" filterData={filterData} value={sides[ 'home' ]} className="flex-grow-1"
                onChange={side => changed({ sides: { ...sides, home: side } })}/>

              <SideForm label="Visitor" filterData={filterData} value={sides[ 'visitor' ]} className="flex-grow-1"
                onChange={side => changed({ sides: { ...sides, visitor: side } })}/>
            </div>
          )
        }
      ]}/>
    );
  }
}

export default class AdminGameModal extends PureComponent {
  static propTypes = {
    game: PropTypes.object,
    filterData: FILTER_DATA_SHAPE.isRequired,
    onChange: PropTypes.func.isRequired
  };

  static defaultProps = {};

  render() {
    const { game, filterData, onChange, ...rest } = this.props;

    return (
      <SaveCancelModal size="lg" title={game && game.id ? 'Edit Game' : 'Add Game'} {...rest}>
        {game && <GameForm filterData={filterData} onChange={onChange} value={game}/>}
      </SaveCancelModal>
    );
  }
}


// WEBPACK FOOTER //
// ./src/js/components/modals/AdminGameModal.jsx